
import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Page} from '../../model/page';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/delay';
import {ToastrService} from 'ngx-toastr';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import {permissions} from '../../permissions';
import { ConfirmationService } from 'primeng/api';
import { TableExtensionService } from '../../services/table-extension.service';
import { SortService } from '../../services/sort.service';
import { AuthService } from '../../auth/auth.service';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs/Subscription';
import { ITeamworkReports } from './interfaces/ITeamworkReports';
import tableCols from './teamwork-reports.cols';
import { DatesService } from 'app/services/dates.service';
import { DisplayFieldPipe } from '../pipes/displayField.pipe';
import {TeamworkReportsService} from '../../services/teamwork-reports.service';

@Component ( {
    providers: [
        TeamworkReportsService,
        AuthService,
        TableExtensionService,
        SortService,
        DatesService,
    ],
    selector: 'app-teamwork-reports',
    templateUrl: './teamwork-reports.component.html',
    styleUrls: ['./teamwork-reports.component.scss'],
})
export class TeamworkReportsComponent implements OnInit, OnDestroy {
    @ViewChild ( 'dataView' ) dataView: ElementRef;
    @ViewChild ( 'actionbar' ) actionbar: ElementRef;
    @ViewChild ( 'content' ) content: ElementRef;
    @ViewChild ( 'tableHeader' ) tableHeader: ElementRef;
    @ViewChild ( 'paginator' ) paginator: ElementRef;
    @ViewChild('tableFilter') tableFilter: ElementRef;
    public loading = false;
    public page = new Page();
    public cols = [];
    public rows = [];
    public selected: any = null;
    public totalRecords = 0;
    public filtersApplied: boolean;
    public scrollHeight = '50px';
    private viewportHeight = 0;
    private viewportHeightListener: boolean;
    private subscriptions: Array<Subscription>;
    public size = 50;
    public userActionLoading = false;
    public sortApplied: boolean;
    public sortField: string;
    public sortOrder: number;
    public rowSelection: number;
    public record: ITeamworkReports;
    public errors: Array<any> = [];
    public action = '';
    protected readonly permissions = permissions;
    public companyList: Array<any> = [];
    public hideTooltip = false;
    public viewFilters = true;
    constructor (
        private teamworkReportsService: TeamworkReportsService,
        private toastr: ToastrService,
        private sortService: SortService,
        private router: Router,
        private route: ActivatedRoute,
        private confirmationService: ConfirmationService,
        private authService: AuthService,
        private title: Title,
        private tableExtensionService: TableExtensionService,
        private datesService: DatesService,
        private displayFieldPipe: DisplayFieldPipe
    ) {
        this.page.pageNumber = 0;
        this.page.size = 50;
        this.title.setTitle ( 'Teamwork Reports' );
    }

    public ngOnInit () {
        this.loading = true;
        this.filtersApplied = false;
        this.sortApplied = false;
        this.sortField = '';
        this.sortOrder = 0;

        this.viewportHeightListener = true;

        const filterParams = this.route.snapshot.queryParams;
        this.parseParams(filterParams);

        this.subscriptions = [];
        this.cols = tableCols;

        this.record = this.teamworkReportsService.initialize();

        this.getData(false);
        this.screenHeightListener();
        this.viewFilters = true;
    }

    public ngOnDestroy (): void {
        this.viewportHeightListener = false;
        for ( const element of this.subscriptions ) {
            if ( element ) {
                element.unsubscribe();
            }
        }
    }

    public getData(reloadWithParams = true) {
        this.setPage({ offset: this.page.pageNumber }, reloadWithParams);
    }

    public setPage(pageInfo, reloadWithParams = true) {
        if (reloadWithParams) {
            this.page.filterParams['page'] = pageInfo.offset ? pageInfo.offset : 0;
            this.page.filterParams['perPage'] = pageInfo.size
                ? pageInfo.size
                : this.size;
            this.router.routeReuseStrategy.shouldReuseRoute = function (
                future: ActivatedRouteSnapshot,
                curr: ActivatedRouteSnapshot
            ) {
                return false;
            };
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: this.page.filterParams,
            });
        } else {
            this.loading = true;
            this.selected = null;
            this.page.pageNumber = pageInfo.offset;
            this.subscriptions.push (
                this.teamworkReportsService.getDataList ( this.page ).subscribe ( result => {
                    this.rows = result.data;
                    this.totalRecords = result.count ? result.count : 0;
                    this.rows['COMPANY_NAME'] = this.rows['COMPANY_NAME'] ? this.rows['COMPANY_NAME'] : this.rows['TC_COMPANY_NAME'];

                    setTimeout ( () => {
                        this.scrollHeight = this.getScrollHeight () + 'px';
                    } );

                    this.loading = false;
                }, () => {
                    this.loading = false;
                    window.dispatchEvent ( new Event ( 'resize' ) );
                    this.toastr.error ( 'Loading error occurred', 'Service Error', {
                        timeOut : 3000,
                    } );
                } )
            );
        }
    }

    public onHover(e, index = null) {
        const aux = e.target.offsetWidth < e.target.scrollWidth;
        this.hideTooltip = !aux;
    }

    public getContentDialogHeight (): number {
        return (
            window.innerHeight - (
                this.actionbar.nativeElement.offsetHeight +
                48
            ) - 77
        );
    }

    public getScrollHeight (): number {
        return this.getContentDialogHeight () - (
            this.tableHeader.nativeElement.offsetHeight
            + this.paginator.nativeElement.offsetHeight + 52 + (
                this.viewFilters ? 52 : 0
            )
        );
    }

    public onPage($event) {
        this.setPage({
            offset: $event.first / $event.rows,
            size: $event.rows
        });
    }

    public onColumnResize ( event ) {
        this.tableExtensionService.onColumnResize ( this.cols, event );
    }

    public onSort(col) {
        if (col.sort) {
            if (this.page.filterParams.hasOwnProperty('SORT_COLUMN')) {
                if (this.page.filterParams['SORT_COLUMN'] === col.field) {
                    if (this.page.filterParams['SORT_DIRECTION'] === 'ASC') {
                        this.page.filterParams['SORT_DIRECTION'] = 'DESC';
                    } else {
                        delete this.page.filterParams['SORT_DIRECTION'];
                        delete this.page.filterParams['SORT_COLUMN'];
                    }
                } else {
                    this.page.filterParams['SORT_COLUMN'] = col.field;
                    this.page.filterParams['SORT_DIRECTION'] = 'ASC';
                }
            } else {
                this.page.filterParams['SORT_COLUMN'] = col.field;
                this.page.filterParams['SORT_DIRECTION'] = 'ASC';
            }

            this.parseSort(this.page.filterParams);
            this.getData();
        }
    }

    private parseSort(filterParams) {
        const sortDetails = this.sortService.parseSort(filterParams);
        this.sortField = sortDetails.sortField;
        this.sortOrder = sortDetails.sortOrder;
    }

    public parseParams(filterParams) {
        if (filterParams !== undefined && filterParams !== null) {
            let showFilters = false;
            let sortApplied = false;
            for (const key of Object.keys(filterParams)) {
                const value = filterParams[key];
                if (value && value.toString() !== '') {
                    if (key.toString() === 'page') {
                        this.page.pageNumber = Number(value);
                    } else if (key.toString() === 'perPage') {
                        this.page.size = Number(value);
                        this.size = Number(value);
                    } else if (
                        key.toString() === 'SORT_COLUMN' ||
                        key.toString() === 'SORT_DIRECTION'
                    ) {
                        this.page.filterParams[key] = value;
                        sortApplied = true;
                    } else {
                        this.page.filterParams[key] = value;
                        showFilters = true;
                    }
                }
            }
            this.viewFilters = showFilters;
            this.filtersApplied = showFilters;
            this.sortApplied = sortApplied;

            this.parseSort(filterParams);
        }
    }

    public refreshClick () {
        this.loading = true;
        this.errors = [];
        this.getData (false);
        this.loading = false;
    }

    public onWindowResize () {
        setTimeout ( () => {
            this.scrollHeight = this.getScrollHeight() + 'px';
            window.dispatchEvent ( new Event ( 'resize' ) );
        }, 150 );
    }

    public screenHeightListener() {
        if (!this.viewportHeightListener) {
            return;
        }
        if (window.innerHeight !== this.viewportHeight) {
            this.viewportHeight = window.innerHeight;
            this.onWindowResize();
        }
        setTimeout(() => {
            this.screenHeightListener();
        }, 500);
    }

    public backClick () {
        this.loading = false;
        this.router.navigate(['/dashboard']);
    }

    public toggleFilterView() {
        this.viewFilters = !this.viewFilters;
        this.onWindowResize();
    }

    public buttonPress(e, col, value) {
        if (e.keyCode === 13) {
            this.filterChanged(col, value);
        }
    }

    public clearFilters() {
        this.page.filterParams = {};
        this.filtersApplied = false;
        this.sortApplied = false;
        this.sortField = '';
        this.sortOrder = 0;
        this.page.pageNumber = 0;
        this.setPage({ offset: this.page.pageNumber }, false);
    }

    public filterChanged(col, value) {
        this.loading = true;

        if (col && value) {
            this.page.filterParams[col] = value;
        }
        this.filtersApplied = Object.keys(this.page.filterParams).length > 0;
        this.page.pageNumber = 0;
        this.setPage({ offset: this.page.pageNumber });
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/teamwork-reports/teamwork-reports.component.ts